// The chart of accounts: how Zoho's narrow account types map onto the buckets
// an accountant reads the chart in.

/**
 * Equity gets its own group rather than being folded into liabilities the way
 * the reference's four buckets would force. Capital accounts are not
 * liabilities, and nearly every client has some.
 */
export type LedgerGroup = "asset" | "liability" | "equity" | "income" | "expense" | "other";

export const GROUP_ORDER: LedgerGroup[] = ["asset", "liability", "equity", "income", "expense", "other"];

export const GROUP_LABEL: Record<LedgerGroup, string> = {
  asset: "Assets",
  liability: "Liabilities",
  equity: "Equity",
  income: "Income",
  expense: "Expenses",
  other: "Other",
};

/**
 * Every account type Zoho documents, not only the seventeen this client
 * happens to use. Anything unmapped lands in "Other" rather than vanishing —
 * a ledger we cannot classify is still a ledger somebody has to look at.
 */
const GROUP_BY_TYPE: Record<string, LedgerGroup> = {
  cash: "asset",
  bank: "asset",
  accounts_receivable: "asset",
  stock: "asset",
  other_current_asset: "asset",
  other_asset: "asset",
  fixed_asset: "asset",
  non_current_asset: "asset",
  intangible_asset: "asset",
  deferred_tax_asset: "asset",
  accumulated_depreciation: "asset",
  payment_clearing_account: "asset",
  input_tax: "asset",

  accounts_payable: "liability",
  credit_card: "liability",
  other_current_liability: "liability",
  other_liability: "liability",
  long_term_liability: "liability",
  non_current_liability: "liability",
  deferred_tax_liability: "liability",
  overseas_tax_payable: "liability",
  output_tax: "liability",

  equity: "equity",

  income: "income",
  other_income: "income",

  expense: "expense",
  cost_of_goods_sold: "expense",
  other_expense: "expense",
};

export function groupForType(accountType: string | null): LedgerGroup {
  return GROUP_BY_TYPE[accountType ?? ""] ?? "other";
}
